"use client"

import { useEffect } from "react"
import type { RefObject } from "react"
import { useLatestRef } from "@/hooks/useLatestRef"
import { usePrefersReducedMotion } from "@/hooks/usePrefersReducedMotion"
import {
    countRollDirection,
    EASE_OUT_EXPO,
    islandSpring,
} from "./islandMotion.utils"

const WATCHED: MutationObserverInit = {
    subtree: true, // boolean-naming-exempt: external contract — MutationObserverInit
    childList: true, // boolean-naming-exempt: external contract — MutationObserverInit
    characterData: true, // boolean-naming-exempt: external contract — MutationObserverInit
    characterDataOldValue: true, // boolean-naming-exempt: external contract — MutationObserverInit
    attributes: true, // boolean-naming-exempt: external contract — MutationObserverInit
    attributeFilter: ["class"],
    attributeOldValue: true, // boolean-naming-exempt: external contract — MutationObserverInit
}

const BLUR_IN: Keyframe[] = [
    { opacity: 0, filter: "blur(4px)", transform: "scale(0.92)" },
    { opacity: 1, filter: "blur(0)", transform: "scale(1)" },
]
const BLUR_IN_MS = 320
const BLUR_IN_DIRECTION = 0
const ROLL_MS = 380

function rollIn(direction: number): Keyframe[] {
    return [
        {
            opacity: 0,
            filter: "blur(3px)",
            transform: `translateY(${direction * 60}%)`,
        },
        { opacity: 1, filter: "blur(0)", transform: "translateY(0)" },
    ]
}

function translateXOf(el: HTMLElement): number {
    const { transform } = getComputedStyle(el)
    return transform === "none" ? 0 : new DOMMatrixReadOnly(transform).m41
}

function isHidden(el: HTMLElement): boolean {
    return el.offsetWidth === 0
}

function wasUnhidden(record: MutationRecord): boolean {
    return (
        record.target instanceof HTMLElement &&
        !record.target.classList.contains("hidden") &&
        (record.oldValue ?? "").split(/\s+/).includes("hidden")
    )
}

function arrivalDirections(
    records: MutationRecord[],
    content: HTMLElement
): Map<HTMLElement, number> {
    const changed = new Map<HTMLElement, number>()
    for (const record of records) {
        if (record.type === "characterData") {
            const el = record.target.parentElement
            if (el) {
                changed.set(
                    el,
                    countRollDirection({
                        before: record.oldValue,
                        after: record.target.textContent,
                    })
                )
            }
        } else if (record.type === "childList") {
            record.addedNodes.forEach(node => {
                const el =
                    node instanceof HTMLElement ? node : node.parentElement
                if (el && !changed.has(el)) {
                    changed.set(el, BLUR_IN_DIRECTION)
                }
            })
        } else if (
            record.target.parentElement === content &&
            record.target instanceof HTMLElement &&
            wasUnhidden(record)
        ) {
            changed.set(record.target, BLUR_IN_DIRECTION)
        }
    }
    const outermost = new Map<HTMLElement, number>()
    changed.forEach((direction, el) => {
        if (el === content || !content.contains(el) || isHidden(el)) return
        let ancestor = el.parentElement
        while (ancestor && ancestor !== content) {
            if (changed.has(ancestor)) return
            ancestor = ancestor.parentElement
        }
        outermost.set(el, direction)
    })
    return outermost
}

export function useIslandMotion({
    islandRef,
    contentRef,
}: {
    islandRef: RefObject<HTMLElement | null>
    contentRef: RefObject<HTMLElement | null>
}) {
    const isReducedMotionPreferred = usePrefersReducedMotion()
    const isReducedMotionPreferredRef = useLatestRef(isReducedMotionPreferred)

    useEffect(() => {
        const island = islandRef.current
        const content = contentRef.current
        if (!island || !content || typeof island.animate !== "function") {
            return
        }
        const offsets = new WeakMap<HTMLElement, number>()
        const glides = new Map<HTMLElement, Animation>()
        let islandWidth = island.offsetWidth
        let morph: Animation | null = null
        let pending: MutationRecord[] = []
        let frame = 0

        const items = () =>
            Array.from(content.children).filter(
                (child): child is HTMLElement => child instanceof HTMLElement
            )
        const centerOffsetOf = (el: HTMLElement) =>
            el.offsetLeft + el.offsetWidth / 2 - content.offsetWidth / 2
        const rememberOffsets = () => {
            for (const item of items()) {
                if (isHidden(item)) offsets.delete(item)
                else offsets.set(item, centerOffsetOf(item))
            }
        }
        const stopMorph = () => {
            morph?.cancel()
            morph = null
            island.style.overflow = ""
        }
        const stopGlides = () => {
            glides.forEach(glide => glide.cancel())
            glides.clear()
        }

        const animateChange = () => {
            frame = 0
            const records = pending
            pending = []
            if (isReducedMotionPreferredRef.current) {
                stopMorph()
                islandWidth = island.offsetWidth
                rememberOffsets()
                return
            }

            const spring = islandSpring()
            const fromWidth = morph ? island.offsetWidth : islandWidth
            const fromOffsets = new Map<HTMLElement, number>()
            for (const item of items()) {
                const offset = offsets.get(item)
                if (offset !== undefined) {
                    fromOffsets.set(item, offset + translateXOf(item))
                }
            }
            stopMorph()
            stopGlides()

            const toWidth = island.offsetWidth
            if (Math.abs(toWidth - fromWidth) > 0.5) {
                // Clipped only while it moves: the menu's focus ring and anything else at the
                // edge must not be cut at rest.
                island.style.overflow = "hidden"
                const current = island.animate(
                    [{ width: `${fromWidth}px` }, { width: `${toWidth}px` }],
                    { duration: spring.durationMs, easing: spring.easing }
                )
                current.onfinish = () => {
                    if (morph !== current) return
                    morph = null
                    island.style.overflow = ""
                }
                morph = current
            }
            islandWidth = toWidth

            for (const item of items()) {
                const from = fromOffsets.get(item)
                if (from === undefined || isHidden(item)) continue
                const shift = from - centerOffsetOf(item)
                if (Math.abs(shift) < 0.5) continue
                glides.set(
                    item,
                    item.animate(
                        [
                            { transform: `translateX(${shift}px)` },
                            { transform: "translateX(0)" },
                        ],
                        { duration: spring.durationMs, easing: spring.easing }
                    )
                )
            }

            arrivalDirections(records, content).forEach((direction, el) => {
                el.animate(
                    direction === BLUR_IN_DIRECTION
                        ? BLUR_IN
                        : rollIn(direction),
                    {
                        duration:
                            direction === BLUR_IN_DIRECTION
                                ? BLUR_IN_MS
                                : ROLL_MS,
                        easing: EASE_OUT_EXPO,
                    }
                )
            })
            rememberOffsets()
        }

        const observer = new MutationObserver(records => {
            pending.push(...records)
            if (!frame) frame = requestAnimationFrame(animateChange)
        })
        observer.observe(content, WATCHED)
        rememberOffsets()

        const onResize = () => {
            const isMoving =
                morph !== null ||
                [...glides.values()].some(
                    glide => glide.playState === "running"
                )
            if (isMoving) {
                if (!frame) frame = requestAnimationFrame(animateChange)
                return
            }
            islandWidth = island.offsetWidth
            rememberOffsets()
        }
        window.addEventListener("resize", onResize)

        return () => {
            observer.disconnect()
            cancelAnimationFrame(frame)
            window.removeEventListener("resize", onResize)
            stopMorph()
            stopGlides()
        }
    }, [islandRef, contentRef])
}
